/**
 * 旅行回忆系统 - 上下文
 * 管理空间状态和当前选中的空间
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { spaceApi } from '../services/travelMemoriesApi';

const TravelMemoriesContext = createContext();

export const useTravelMemories = () => {
  const context = useContext(TravelMemoriesContext);
  if (!context) {
    throw new Error('useTravelMemories必须在TravelMemoriesProvider内使用');
  }
  return context;
};

export const TravelMemoriesProvider = ({ children }) => {
  const [spaces, setSpaces] = useState([]);
  const [currentSpace, setCurrentSpace] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 加载用户的所有空间
  const loadSpaces = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await spaceApi.getSpaces();
      
      // 调试日志
      console.log('【TravelMemoriesContext】API响应:', response);
      
      // 处理响应：axios拦截器已经返回了 response.data
      // 所以 response 就是 { success: true, data: [...] }
      let spacesData = [];
      if (response && response.success) {
        spacesData = response.data || [];
      } else if (Array.isArray(response)) {
        // 如果响应直接是数组（不应该发生，但做兼容处理）
        spacesData = response;
      }
      
      console.log('【TravelMemoriesContext】解析后的空间数据:', spacesData);
      console.log('【TravelMemoriesContext】空间数量:', spacesData.length);
      setSpaces(spacesData);

      // 首页始终显示空间列表，不自动选择空间
      // 只有当用户明确选择了一个空间后，才显示该空间的旅行内容
      if (currentSpace) {
        // 如果已经有选中的空间，更新其信息
        // 严格验证：确保spaceId在当前空间列表中，且格式正确
        const currentSpaceId = currentSpace.id || currentSpace._id;
        const updatedSpace = spacesData.find(s => {
          const spaceId = s.id || s._id;
          return spaceId && currentSpaceId && spaceId.toString() === currentSpaceId.toString();
        });
        if (updatedSpace) {
          // 验证通过，更新空间信息
          setCurrentSpace(updatedSpace);
          localStorage.setItem('travelMemories_currentSpace', JSON.stringify(updatedSpace));
        } else {
          // 如果当前空间不在列表中，清除它（可能是localStorage中的旧数据）
          console.warn('【TravelMemoriesContext】当前空间不在列表中，已清除:', currentSpaceId);
          setCurrentSpace(null);
          localStorage.removeItem('travelMemories_currentSpace');
        }
      }
      // 不再自动选择空间，让首页始终显示空间列表
    } catch (err) {
      console.error('【TravelMemoriesContext】加载空间列表失败:', err);
      console.error('【TravelMemoriesContext】错误详情:', {
        message: err.message,
        response: err.response,
        responseData: err.response?.data,
        status: err.response?.status
      });
      const errorMessage = err.response?.data?.message || err.message || '加载空间列表失败';
      setError(errorMessage);
      
      // 如果是403错误（权限不足），显示友好提示
      if (err.response?.status === 403) {
        console.warn('权限不足，无法访问旅行回忆系统:', errorMessage);
        // 不跳转到登录页，只设置错误信息
      }
    } finally {
      setLoading(false);
    }
  };

  // 选择空间
  const selectSpace = (space) => {
    setCurrentSpace(space);
    localStorage.setItem('travelMemories_currentSpace', JSON.stringify(space));
  };

  // 刷新空间列表
  const refreshSpaces = () => {
    return loadSpaces();
  };

  // 初始化：加载空间列表，但不自动恢复选中的空间
  // 首页始终显示空间列表，用户需要手动选择空间
  useEffect(() => {
    // 加载空间列表
    loadSpaces();
    // 清除之前保存的空间选择，让首页显示空间列表
    localStorage.removeItem('travelMemories_currentSpace');
    setCurrentSpace(null);
  }, []);

  const value = {
    spaces,
    currentSpace,
    loading,
    error,
    selectSpace,
    refreshSpaces,
    loadSpaces
  };

  return (
    <TravelMemoriesContext.Provider value={value}>
      {children}
    </TravelMemoriesContext.Provider>
  );
};
